import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';
import { PublicFooter } from '@components/application/PublicFooter';
import { PublicNavbar } from '@components/application/PublicNavbar';
import LoadingPageOverlay from '@components/common/LoadingPageOverlay';
import { StalledLoadPage } from '../ErrorPages';
import { useStalledAfter } from '@hooks/useStalledAfter';
import useAuth, { AuthContextType } from '@contexts/useAuth/useAuth';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import styles from './Layout.module.css';
import { UserNavbar, useNavbarPolicies, createNavbarPolicies } from '@domains/navbar';
import {
  useNavigationDispatch,
  useNavigationPolicies,
  createNavigationPolicies
} from '@domains/navigation';
import {
  useAIAssistantPolicies,
  createAIAssistantPolicies,
  useAIAssistantActions
} from '@domains/aiAssistant';
import {
  useBatchProcessesSharedPolicies,
  createBatchProcessesSharedPolicies
} from '@domains/batchProcesses';
import {
  useAutomationSharedPolicies,
  createAutomationSharedPolicies
} from '@domains/automation';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useActiveFeatureFlags, useFeatureFlagsReady } from '@bett3r-dev/teselly-feature-flags';
import {
  useOnboardingPolicies,
  useOnboardingSelector,
  createOnboardingPolicies,
  onboardingSelectors
} from '@domains/onboarding';
import useUserPreferences from '@contexts/useUserPreferences/useUserPreferences';
import {
  OnboardingWizard,
  useOnboardingWizardPolicies,
  useOnboardingWizardDispatch,
  createOnboardingWizardPolicies
} from '@domains/onboardingWizard';
import {
  useSharedIntegrationsPolicies,
  createSharedIntegrationsPolicies,
  getSharedIntegrationsState,
  RevokedIntegrationsBanner,
  useRevokedIntegrationsBannerPolicies,
  createRevokedIntegrationsBannerPolicies
} from '@domains/integrations';
import { createLLMService } from '@infrastructure/aiAssistant/aiAssistantService';
import { registerNavigate } from '@commandDrivenStateEngine/core/storeRegistry';
import { ProvideNotifications } from '@contexts/useNotifications/useNotifications';
import { AIAssistantPanel } from '@components/application/AIAssistantChat/AIAssistantPanel';

/**
 * TV1-1903: First-entry onboarding wizard, mounted at the composition root.
 *
 * Gating is purely here (flag + `selectIsOnboardingRequired`) — never via
 * conditional commands in an aggregate — so nothing leaks into generated
 * artifacts. The onboarding + shared-connector policies are registered ONCE,
 * here, per the CDSE single-registration-site rule. The shared-connector
 * load policy is needed so the invoicing step has its connector catalog even
 * before the user ever visits the Integrations page.
 */
function OnboardingGate() {
  const { backend } = useBackend() as BackendContextType;
  const userPrefs = useUserPreferences();

  const onboardingRequired = useOnboardingSelector(
    onboardingSelectors.selectIsOnboardingRequired
  );
  const manuallyOpened = useOnboardingSelector(
    onboardingSelectors.selectManuallyOpened
  );

  const getConnectors = useMemo(
    () => () => getSharedIntegrationsState().connectors,
    []
  );

  const sharedPolicies = useMemo(
    () => createSharedIntegrationsPolicies( backend ),
    [backend]
  );
  useSharedIntegrationsPolicies( sharedPolicies );

  const onboardingPolicies = useMemo(
    () => createOnboardingPolicies( backend, getConnectors ),
    [ backend, getConnectors ]
  );
  useOnboardingPolicies( onboardingPolicies );

  const wizardDispatch = useOnboardingWizardDispatch();

  const wizardPolicies = useMemo(
    () => createOnboardingWizardPolicies( backend ),
    [backend]
  );
  useOnboardingWizardPolicies( wizardPolicies );

  // One shot per mount. `dismissBillingEntityWizard` persists server-side but is
  // async; without this the effect can re-enter before the preference lands.
  const alreadyOpened = useRef( false );

  const isLoadingPrefs = userPrefs?.isLoading ?? true;
  const persistentlyDismissed =
    userPrefs?.preferences?.billingEntityWizard?.status === 'dismissed';
  const shouldOpen =
    !isLoadingPrefs && ( onboardingRequired || manuallyOpened ) && !persistentlyDismissed;

  /**
   * First login opens the FULL-SCREEN BENTO WIZARD — not the ARCA / billing
   * entity modal, and not the assistant in fullscreen.
   *
   * ⚠️ The conversation is NOT seeded here. WHICH greeting is right depends on
   * whether the account already has a sales channel — signing up with Mercado
   * Libre connects one and starts its import during sign-in — and that fact
   * arrives with the integrations snapshot, after this effect runs. Seeding here
   * would ask "¿en qué canal vendés?" of a user whose channel is already
   * importing. `connectionWatchPolicy` owns the decision and both greetings.
   *
   * Closing the wizard at any moment is still lossless: the conversation holds
   * the connected channel and the live `import-progress` block, so the floating
   * assistant shows the same thing.
   *
   * It opens ONCE. Marking the preference immediately (rather than on close) is
   * deliberate and matches the agreed behaviour: skipping means it does not come
   * back. The way back is the Tessy button in the navbar, plus the setup
   * checklist on the dashboard and its "volver a mostrarlo" control in Account
   * settings.
   *
   * ⚠️ NAMING DEBT: `billingEntityWizard` is the persisted flag, and it now means
   * "the first-login onboarding was shown". Renaming it needs a
   * clients-management preference migration, so it is deliberately NOT done here.
   */
  useEffect(() => {
    if ( !shouldOpen || alreadyOpened.current ) return;
    alreadyOpened.current = true;

    wizardDispatch(
      { type: 'onboardingWizard/ui/open', payload: {}},
      { source: 'application' }
    );
    void userPrefs?.dismissBillingEntityWizard();
  }, [ shouldOpen, wizardDispatch, userPrefs ]);

  return <OnboardingWizard />;
}

/**
 * Inner layout component that initializes navbar and navigation policies.
 * Separated to ensure hooks are called in the correct order.
 */
function AuthenticatedLayout() {
  const { backend, realTimeSession } = useBackend() as BackendContextType;
  const navigate = useNavigate();
  const location = useLocation();
  const navigationDispatch = useNavigationDispatch();

  // Initialize BOTH policy sets
  useNavbarPolicies( createNavbarPolicies( backend ));
  useNavigationPolicies( createNavigationPolicies( navigate ));

  // AI assistant policies
  const service = useMemo(() => createLLMService( backend ), [backend]);
  const aiPolicies = useMemo(() => createAIAssistantPolicies( service, backend, realTimeSession ), [ service, backend, realTimeSession ]);
  useAIAssistantPolicies( aiPolicies );

  // Batch processes shared policies (always-on for navbar dropdown)
  const batchProcessesPolicies = useMemo(
    () => createBatchProcessesSharedPolicies( backend ),
    [backend]
  );
  useBatchProcessesSharedPolicies( batchProcessesPolicies );

  // Automation shared policies (always-on for navbar dropdown)
  const automationSharedPolicies = useMemo(
    () => createAutomationSharedPolicies( backend ),
    [backend]
  );
  useAutomationSharedPolicies( automationSharedPolicies );

  /**
   * TV1-2446 slice 7 — the org-wide revoked-integrations banner's ONE registration site.
   * Subscribes directly to `ExternalSystemsIntegrations` (not the legacy `ProvideIntegrations`
   * context), independent of the channels/erps subdomain stores, so it sees every kind of
   * integration and every delete.
   */
  const revokedIntegrationsBannerPolicies = useMemo(
    () => createRevokedIntegrationsBannerPolicies( backend ),
    [backend]
  );
  useRevokedIntegrationsBannerPolicies( revokedIntegrationsBannerPolicies );

  // TV1-1903: onboarding wizard is gated off by default behind a feature flag.
  const onboardingWizardEnabled = useBooleanFlagValue( 'onboarding-wizard', false );

  /**
   * TV1-2377 slice 11 — the chat entry, site 2/3 of the kill switch.
   *
   * `AIAssistantPanel` is the ONE mount point for the assistant surface app-wide
   * (the navbar's Tessy button toggles `isPanelOpen`; `OnboardingWizard`'s
   * `closeAndHandOver` opens it directly via `openPanel()`). Gating only the
   * navbar button is not a kill switch: any OTHER caller that flips
   * `isPanelOpen` still reaches a live chat. Reuses the EXISTING `aiAssistant`
   * flag — no new flag is minted.
   *
   * `useFeatureFlagsReady()` gates alongside the value, as the draft block's
   * read also does. It is BELT-AND-BRACES rather than load-bearing: before the
   * account-scoped snapshot arrives `useActiveFeatureFlags()` returns `[]`, so
   * an uninitialized store already reads as OFF, never as "unrestricted" — which
   * is why the navbar's own read (value only, no readiness check) is safe too.
   * The explicit readiness gate also refuses a stale non-empty array from a
   * previous account, which the value alone would not.
   */
  const aiAssistantFlagsReady = useFeatureFlagsReady();
  const aiAssistantActiveFlags = useActiveFeatureFlags();
  const aiAssistantEnabled = aiAssistantFlagsReady && aiAssistantActiveFlags.includes( 'aiAssistant' );

  // Register navigate function for AI plan executor
  useEffect(() => registerNavigate( navigate ), [navigate]);

  // Sync React Router location to navigation state
  useEffect(() => {
    navigationDispatch(
      {
        type: 'navigation/location/update',
        payload: { path: location.pathname, params: location.state || {}}
      },
      { source: 'url' }
    );
  }, [ location, navigationDispatch ]);

  return (
    <>
      <RevokedIntegrationsBanner />
      <UserNavbar />
      <Outlet />
      {aiAssistantEnabled ? <AIAssistantPanel /> : null}
      {onboardingWizardEnabled ? <OnboardingGate /> : null}
    </>
  );
}

export default function Layout() {
  const { user, capabilitiesReady } = useAuth() as AuthContextType;
  const capabilitiesStalled = useStalledAfter( !!user && !capabilitiesReady );
  return (
    <div className={styles.container} id='tour-app'>
      {user ? (
          // Hold the authenticated shell ( navbar + page ) behind a loader until
          // the capability map has hydrated. Rendering before then makes every
          // capability-gated tab/route evaluate against an empty `{}` map, so the
          // menu briefly shows only Dashboard ( or flickers ) until caps arrive.
          // `capabilitiesReady` flips true once the query settles ( success OR
          // failure ), so this never hangs.
          capabilitiesReady ? (
            <ProvideNotifications>
              <AuthenticatedLayout />
            </ProvideNotifications>
          ) : capabilitiesStalled ? (
            /*
              TV1-2369 — the capabilities query has no unhappy path. If it never
              settles this loader spins forever, nothing throws, and the user has
              nothing to click. After 15s, say so and offer a reload.
            */
            <StalledLoadPage />
          ) : (
            <LoadingPageOverlay>
              <div style={{ height: '100vh' }} />
            </LoadingPageOverlay>
          )
      ) : (
        <>
          <PublicNavbar />
          <Outlet />
          <PublicFooter />
        </>
      )}
    </div>
  );
}
